import { useEffect, useRef } from 'react';
import { usePage } from '@inertiajs/react';
import { subscribeToChannel } from '../utils/centrifugo';
import { dispatchCartCount } from '../utils/cartBadge';

const tabId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

export function getTabId(): string {
    return tabId;
}

interface CartSyncData {
    cart: unknown[];
    count: number;
    tab_id: string | null;
    updated_at: string;
}

interface SharedProps {
    auth: { user: { id: number } | null };
    centrifugoUrl: string | null;
}

export function useCartSync(onCartUpdate?: (count: number) => void): void {
    const { auth, centrifugoUrl } = usePage<SharedProps>().props;
    const callbackRef = useRef(onCartUpdate);
    callbackRef.current = onCartUpdate;

    useEffect(() => {
        const userId = auth?.user?.id;
        if (!userId || !centrifugoUrl) return;

        const channel = `$av:user-cart.${userId}`;

        const unsubscribe = subscribeToChannel(centrifugoUrl, channel, (data: CartSyncData) => {
            // Ignore messages from this tab
            if (data.tab_id === tabId) return;

            dispatchCartCount(data.count);

            // Notify page-level components (e.g. Cart/Index) that an external sync arrived
            window.dispatchEvent(new CustomEvent('cart-sync-external', { detail: data }));

            callbackRef.current?.(data.count);
        });

        return unsubscribe;
    }, [auth?.user?.id, centrifugoUrl]);
}
